import { Injectable } from '@angular/core';

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class AppCacheService {
  private readonly entries = new Map<string, CacheEntry<unknown>>();
  private readonly pending = new Map<string, Promise<unknown>>();

  async readThrough<T>(key: string, fetcher: () => Promise<T>, ttlMs = 60_000): Promise<T> {
    const cached = this.get<T>(key);
    if (cached !== undefined) {
      return cached;
    }

    const inFlight = this.pending.get(key);
    if (inFlight) {
      return inFlight as Promise<T>;
    }

    const pendingRequest = Promise.resolve()
      .then(fetcher)
      .then((value) => {
        this.set(key, value, ttlMs);
        this.pending.delete(key);
        return value;
      })
      .catch((error) => {
        this.pending.delete(key);
        throw error;
      });

    this.pending.set(key, pendingRequest);
    return pendingRequest;
  }

  get<T>(key: string): T | undefined {
    const entry = this.entries.get(key);
    if (!entry) {
      return undefined;
    }

    if (entry.expiresAt <= Date.now()) {
      this.entries.delete(key);
      return undefined;
    }

    return entry.value as T;
  }

  set<T>(key: string, value: T, ttlMs = 60_000): T {
    this.entries.set(key, {
      value,
      expiresAt: Date.now() + ttlMs,
    });

    return value;
  }

  invalidate(key: string): void {
    this.entries.delete(key);
    this.pending.delete(key);
  }

  invalidatePrefix(prefix: string): void {
    for (const key of this.entries.keys()) {
      if (key.startsWith(prefix)) {
        this.entries.delete(key);
      }
    }

    for (const key of this.pending.keys()) {
      if (key.startsWith(prefix)) {
        this.pending.delete(key);
      }
    }
  }

  clear(): void {
    this.entries.clear();
    this.pending.clear();
  }
}
